import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { SocialGroupCard } from "@/components/sections/social-groups/SocialGroupCard";
import { useFrontendSocialGroups } from "@/hooks/useFrontendSocialGroups";
import { Breadcrumb } from "@/components/ui/breadcrumb-nav";
import { useAuth } from "@/hooks/useAuth";
import { useInteractionSounds } from "@/hooks/useInteractionSounds";
import { motion } from "framer-motion";

// Add Your Channel Card
const AddChannelCard = ({ isRTL }: { isRTL: boolean }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { playHoverSound } = useInteractionSounds();

  const handleClick = () => {
    if (!user) {
      navigate("/auth");
    } else {
      navigate("/packages");
    }
  };

  return (
    <motion.div
      className="relative bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/20 dark:to-green-900/30 rounded-xl overflow-hidden cursor-pointer border-2 border-dashed border-emerald-300 dark:border-emerald-600 min-h-[280px] flex items-center justify-center"
      style={{
        boxShadow: '8px 8px 16px rgba(0,0,0,0.08), -8px -8px 16px rgba(255,255,255,0.9)',
      }}
      whileHover={{ y: -5, scale: 1.02 }}
      onMouseEnter={() => playHoverSound()}
      onClick={handleClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-center p-6">
        <motion.div 
          className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg"
          whileHover={{ rotate: 10 }}
        >
          <MessageCircle className="w-10 h-10 text-white" />
        </motion.div>
        <h3 className="text-xl font-bold text-emerald-700 dark:text-emerald-300 mb-2">
          {isRTL ? "أضف قناتك أو حسابك" : "Add Your Channel"}
        </h3>
        <p className="text-sm text-emerald-600/70 dark:text-emerald-400/70 mb-4">
          {isRTL ? "شارك حسابك مع المجتمع" : "Share your account with the community"}
        </p>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-full text-sm font-medium shadow-md">
          {isRTL ? "ابدأ الآن" : "Start Now"}
          {isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
        </div>
      </div>
    </motion.div>
  );
};

const SocialGroupsPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const { groups, isLoading } = useFrontendSocialGroups();

  return (
    <div className={`min-h-screen bg-background ${isRTL ? "rtl" : "ltr"}`}>
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="flex items-center gap-2"
            >
              <ArrowLeft size={16} className={isRTL ? "rotate-180" : ""} />
              {isRTL ? "الرئيسية" : "Home"}
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <MessageCircle className="text-emerald-500" size={24} />
              </div>
              <h1 className="text-xl font-bold">
                {isRTL ? "مجموعات التواصل" : "Social Groups"}
              </h1>
            </div>
            <div className="w-[100px]"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        <Breadcrumb />

        <div className="mb-6">
          <p className="text-muted-foreground text-center max-w-2xl mx-auto">
            {isRTL
              ? "انضم إلى مجموعات التواصل الاجتماعي للبقاء على اطلاع بأحدث الفعاليات والأخبار"
              : "Join our social media groups to stay updated with the latest events and news"}
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-48 bg-gray-100 rounded-xl animate-pulse"
              />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-16">
            <MessageCircle className="mx-auto text-muted-foreground mb-4" size={48} />
            <p className="text-muted-foreground">
              {isRTL ? "لا توجد مجموعات حالياً" : "No groups available"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AddChannelCard isRTL={isRTL} />
            {groups.map((group) => (
              <SocialGroupCard key={group.id} group={group} isRTL={isRTL} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialGroupsPage;
